<template>
	<Teleport to="body" v-if="chain.visible.value"><component :is="tag" :class="['boat', tailClass]" v-bind="$attrs" :style="style" ref="elm"
				  @mousedown="blockClickEvent" @mouseenter="boatEnter" @mouseleave="boatLeave" @focusin="focusIn" @focusout="focusOut"><slot></slot></component></Teleport>
</template>

<script setup lang="ts">
import {ref, computed, watch, nextTick, onMounted, onUnmounted, type CSSProperties} from "vue";
import type {Chain} from "@/ui/paneltree/PanelTree";
import {getElementMeasure} from "@fd/helpers/DomUtils";
import {debounce} from "@/utils/Debounce.ts";

defineOptions({
	inheritAttrs: false
});

const props = withDefaults(defineProps<{
	tag?: any,
	chain: Chain,
	verticalCorrection?: boolean
}>(), {
	tag: "div",
	verticalCorrection: false
});

//const emit = defineEmits([""]);
const elm = ref<HTMLElement | null>(null);


/* Calculate real root element (works for components) */
const root = computed<HTMLElement | null>(() => {
	const elementOrComponent = elm.value;
	if (elementOrComponent == null) return null;
	return elementOrComponent.$el ? elementOrComponent.$el : elementOrComponent;
});

function element():HTMLElement|null {
	return root.value
}

function boatEnter(e:MouseEvent):void {
	props.chain.mouseInBout.value = true;
}

function boatLeave(e:MouseEvent):void {
	props.chain.mouseInBout.value = false;
}

function focusIn(e:MouseEvent):void {
	props.chain.focusInBout.value = true;
}

function focusOut(e:MouseEvent):void {
	props.chain.focusInBout.value = false;
}

const tailClass = computed(() => {
	const tailPlacement = props.chain.rule?.value?.tail;
	if (tailPlacement && tailPlacement !== "none") {
		return "tail-"+tailPlacement;
	}
	return null;
})

defineExpose({ element });

const width = ref<number>(0);
const height = ref<number>(0);

const style = computed<CSSProperties>(() => {
	const s:CSSProperties = {};
	const ch = props.chain;
	s.position = ch.fixed.value ? "fixed" : "absolute";

	// Provide variables
	if (ch.anchor.value) {
		s['--anchor-width'] = ch.anchor.value.width + "px";
		s['--anchor-height'] = ch.anchor.value.height + "px";
		s.visibility = "visible";	// display only if anchor calculation is available
	}

	if (typeof ch.rule.value?.spaceLimit === "number") {
		s['--space-limit'] = ch.rule.value.spaceLimit + "px";
	}

	let top = ch.y.value;
	let left = ch.x.value;

	const r = ch.rule.value;
	if (r) {
		left -= width.value * (r.bh / 100);
		top -= height.value * (r.bv / 100);
	}

	// Protect from leaking outside the screen
	let minLeftEdge, maxRightEdge, minTopEdge, maxBottomEdge;
	const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
	const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
	if (ch.fixed.value) {
		minTopEdge = 0;
		maxBottomEdge = viewportHeight;
		minLeftEdge = 0;
		maxRightEdge = viewportWidth;
	} else {
		const scrollTop = window.scrollY || document.documentElement.scrollTop;
		const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
		minTopEdge = scrollTop;
		maxBottomEdge = scrollTop + viewportHeight;
		minLeftEdge = scrollLeft;
		maxRightEdge = scrollLeft + viewportWidth;
	}
	if ((left + width.value) > maxRightEdge) left -= (left + width.value) - maxRightEdge;
	if (left < minLeftEdge) left += minLeftEdge - left;
	if (props.verticalCorrection) {
		// This attempt to correct panel possion caused problems with boat jumping around the page and invoking unwanted mouseenter/leave events
		// That's why it's disabled by default'
		if ((top + height.value) > maxBottomEdge) top -= (top + height.value) - maxBottomEdge;
		if (top < minTopEdge) top += minTopEdge - top;
	}

	s.top = top + "px";
	s.left = left + "px";

	// DEBUG block
	//console.log("-- " + minLeftEdge + "-" + maxRightEdge + "  | " + minTopEdge + "-" + maxBottomEdge+"   fixed="+ch.fixed.value);
	// let outlineWidth = 0;
	// let outlineColor = "gray";
	// if (props.chain.mouseIn.value) {
	// 	outlineWidth = 1;
	// 	outlineColor = "orange";
	// 	if (props.chain.mouseInBout.value) outlineColor = "greenyellow";
	// }
	// if (props.chain.focusIn.value) {
	// 	outlineWidth = 2;
	// 	if (props.chain.focusInBout.value) outlineWidth = 4;
	// }
	// if (outlineWidth > 0) s.outline = outlineWidth + "px solid " + outlineColor;

	//console.log("Bout CSS: "+JSON.stringify(s));
	return s;
});

function blockClickEvent(e:MouseEvent):void {
	// Prevent closing all panels on body-click
	e.stopPropagation();
}

let resizeObserver = null;
let boat:HTMLElement|null = null;	// observed element

function _updateBoatMeasure() {
	const rect = boat.getBoundingClientRect();
	width.value = rect.width;
	height.value = rect.height;
}

const updateBoatMeasure = debounce(_updateBoatMeasure, 100);

function startObserving(el:HTMLElement) {
	if (resizeObserver == null) {
		resizeObserver = new ResizeObserver(updateBoatMeasure);
		resizeObserver.observe(el);
		boat = el;
		updateBoatMeasure();
	}
}

function stopObserving() {
	if (resizeObserver != null) {
		resizeObserver.disconnect();
		resizeObserver = null;
		boat = null;
	}
}

watch(() => root.value, (el) => {
	props.chain.boatElement.value = el;
	if (el) startObserving(el);
	else stopObserving();
});

onUnmounted(() => {
	stopObserving();
});
</script>

<style>
.boat {
	box-sizing: border-box;
	visibility: hidden;
	top: -4000px;
}


/** All tails */
.tail-top-left, .tail-top-center, .tail-top-right, .tail-bottom-left, .tail-bottom-center,
.tail-bottom-right, .tail-left-top, .tail-left-center, .tail-left-bottom,
.tail-right-top, .tail-right-center, .tail-right-bottom {
	--tail-len: 8px;
	overflow: visible;

	&:before, &:after {
		display: inline-block;
		content: '';
		width: 0;
		height: 0;
		position: absolute;
		border-style: solid;
		pointer-events: none;
		user-select: none;
		-webkit-user-drag: none;
	}
	&:before {
		z-index: calc(var(--drop-z-index) + 1000);
		border-width: var(--tail-len);
		border-color: transparent;
	}
	&:after {
		z-index: calc(var(--drop-z-index) + 1001);
		border-width: calc(var(--tail-len) - 1px);
		border-color: transparent;
	}
}

/* sqrt(2) = 1.4142px, double 2.8284px */
/** Top edge */
.tail-top-left, .tail-top-center, .tail-top-right {
	transform: translateY(var(--tail-len));
	&:before {
		border-bottom-color: var(--border-color);
		top: calc(-2 * var(--tail-len));
	}
	&:after {
		border-bottom-color: var(--bg);
		top: calc(-2 * var(--tail-len) + 2.5px); /* 2.5 looks best on apple devices */
	}
}

.tail-top-left, .tail-bottom-left {
	&:before {
		left: 8px;
	}
	&:after {
		left: 9px;
	}
}

.tail-top-center, .tail-bottom-center {
	&:before {
		left: calc(50% - var(--tail-len));
	}
	&:after {
		left: calc(50% - var(--tail-len) + 1px);
	}
}

.tail-top-right, .tail-bottom-right {
	&:before {
		right: 8px;
	}
	&:after {
		right: 9px;
	}
}

/** Left edge */
.tail-left-top, .tail-left-center, .tail-left-bottom {
	transform: translateX(var(--tail-len));
	&:before {
		border-right-color: var(--border-color);
		left: calc(-2 * var(--tail-len));
	}
	&:after {
		border-right-color: var(--bg);
		left: calc(-2 * var(--tail-len) + 2.5px);
	}
}

.tail-left-top, .tail-right-top {
	&:before {
		top: 8px;
	}
	&:after {
		top: 9px;
	}
}

.tail-left-center, .tail-right-center {
	&:before {
		top: calc(50% - var(--tail-len));
	}
	&:after {
		top: calc(50% - var(--tail-len) + 1px);
	}
}

.tail-left-bottom, .tail-right-bottom {
	&:before {
		bottom: 8px;
	}
	&:after {
		bottom: 9px;
	}
}

/** Right edge */
.tail-right-top, .tail-right-center, .tail-right-bottom {
	transform: translateX(calc(-1 * var(--tail-len)));
	&:before {
		border-left-color: var(--border-color);
		right: calc(-2 * var(--tail-len));
	}
	&:after {
		border-left-color: var(--bg);
		right: calc(-2 * var(--tail-len) + 2.5px);
	}
}

/** Bottom edge */
.tail-bottom-left, .tail-bottom-center, .tail-bottom-right {
	transform: translateY(calc(-1 * var(--tail-len)));
	&:before {
		border-top-color: var(--border-color);
		bottom: calc(-2 * var(--tail-len));
	}
	&:after {
		border-top-color: var(--bg);
		bottom: calc(-2 * var(--tail-len) + 2.5px);
	}
}

.debug-cls {
	outline: 2px solid greenyellow;
}

.debug-cls2 {
	outline: 2px solid orange;
}
</style>
